import { Injectable, inject } from '@angular/core';
import { ResourceDependenciesModel, ResourceActivitiesModel } from './resource.model';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable, throwError } from 'rxjs';
import { map, catchError } from 'rxjs/operators';
import { environment } from '@environments/environment';
import {
  ApiResultResponseOfBookingPeriodCalculatePriceAndBookingPeriodCalculatePriceStatus,
  ApiResultResponseOfBookingPeriodCheckResponseAndPeriodCheckStatus,
  ApiResultResponseOfCompleteBookingResponseAndCompleteBookingStatus,
  BookingPeriodEntry,
  BookingPeriodEntryRequest,
  BookingPeriodExpanded,
  ResourceEntry,
} from './model/models';
import {
  Activity,
  ApiDateTime,
  Booking,
  BookingPeriod,
  BookingRequest,
  CalendarDay,
  Code,
  Resource,
  toApiDateTime,
} from '@shared/models/models';
import { AuthenticationService } from '@app/core/authentication/authentication.service';
import { LoggedUserModel } from '@app/core/authentication/logged-user.model';
import { Payment } from './model/payment';
import { RefundPayload } from './model/refundRequest';
import { CalendarTimeslot } from './model/calendar-timeslot';
import { BookingPeriodRequest } from '@app/features/associations/models/bookingPeriodAssociation';
import { PSPResult } from '@shared/models/PSPResult';
import { PSPPayload } from '@shared/models/PSPPayload';
const APIURL = environment.apiUrl;
interface TimeslotSlotResponse {
  id: number;
  resourceId: number;
  /**
   * Deliberately not `ApiDateTime`: the mapping below truncates these to 19 characters, which only
   * makes sense if the endpoint can send more than that — fractional seconds, or an offset. What
   * reaches the app as an `ApiDateTime` is `CalendarTimeslot`, after that slice.
   */
  start: string;
  end: string;
  price: number;
  activityId: number | null;
  locationId: number | null;
  isAvailable: boolean;
  activityIds: number[];
  /** Absent from an API from before the booker-type warning (#949); read as null. */
  bookerTypeWarning?: string | null;
}
@Injectable()
export class CalenderService {
  private http = inject(HttpClient);
  private authenticationService = inject(AuthenticationService);

  currentUser!: LoggedUserModel;
  constructor() {
    this.authenticationService.loggedUserSubject.subscribe({
      next: (loggedUser) => {
        this.currentUser = loggedUser;
      },
    });
  }
  /*
    API: Resources
  */
  getResources(locationId?: number, activityId?: number): Observable<Resource[]> {
    let params = '?';
    if (locationId) params += `&location_id=${locationId}`;
    if (activityId) params += `&activity_id=${activityId}`;
    return this.http.get<Resource[]>(`${APIURL}/resources/${params}`).pipe(
      map((data) => {
        return data;
      }),
    );
  }
  getRelatedResourcesByActivity(resourceId: number, activityId: number): Observable<Resource[]> {
    const param = '?expand=parent';
    return this.http
      .get<Resource[]>(`${APIURL}/resources/${resourceId}/activities/${activityId}/resources/${param}`)
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }
  getResourcesDependencies(locationId?: number): Observable<ResourceDependenciesModel[]> {
    const paramLocation = locationId ? `?location_id=${locationId}` : '';
    return this.http.get<ResourceDependenciesModel[]>(`${APIURL}/resources/dependencies/${paramLocation}`).pipe(
      map((data) => {
        return data;
      }),
      catchError(() => {
        return throwError('Something went wrong!');
      }),
    );
  }
  getResourcesActivities(locationId?: number): Observable<ResourceActivitiesModel[]> {
    const paramLocation = locationId ? `?location_id=${locationId}` : '';
    return this.http.get<ResourceActivitiesModel[]>(`${APIURL}/resources/activities/${paramLocation}`).pipe(
      map((data) => {
        return data;
      }),
      catchError(() => {
        return throwError('Something went wrong!');
      }),
    );
  }
  getResourceActivities(resourceId: number): Observable<Activity[]> {
    return this.http.get<Activity[]>(`${APIURL}/resources/${resourceId}/activities`).pipe(map((data) => data));
  }
  getResourceEntries(
    resourceId: number,
    userId: number,
    // Interpolated into the query string below, so it must be the API's own format — a Date would
    // send "Sat Sep 13 2026 00:00:00 GMT+0200 (CEST)".
    start: ApiDateTime,
    end: ApiDateTime,
  ): Observable<ResourceEntry[]> {
    let paramUser = '?';
    if (userId > 0) {
      paramUser += `&userId=${userId}`;
    }
    if (start && end) {
      paramUser += `&start=${start}&end=${end}`;
    }
    return this.http.get<ResourceEntry[]>(APIURL + `/resources/${resourceId}/entries${paramUser}`).pipe(
      map((data) => {
        return data;
      }),
      catchError(() => {
        return throwError('Something went wrong!');
      }),
    );
  }
  /*
    API: Locations
  */
  /*
    API: Activities
  */
  getActivities(locationId?: number): Observable<Activity[]> {
    let paramLocation = '';
    if (locationId) {
      paramLocation = `?location_id=${locationId}`;
    }
    return this.http.get<Activity[]>(`${APIURL}/activities/${paramLocation}`).pipe(
      map((activities) => {
        return activities;
      }),
      catchError(() => {
        return throwError('Something went wrong!');
      }),
    );
  }
  /* 
    API: Bookings
  */
  getBooking(bookingId: number): Observable<Booking> {
    return this.http.get<Booking>(APIURL + `/bookings/${bookingId}/`).pipe(
      map((data) => {
        return data;
      }),
      catchError(() => {
        return throwError('Something went wrong!');
      }),
    );
  }
  createBooking(booking: BookingRequest): Observable<Booking> {
    return this.http.post<Booking>(APIURL + '/bookings', booking).pipe(
      map((result) => {
        return result;
      }),
    );
  }
  updateBooking(bookingId: number, booking: BookingRequest): Observable<Booking> {
    return this.http.put<Booking>(`${APIURL}/bookings/${bookingId}`, booking).pipe(
      map((result) => {
        return result;
      }),
    );
  }
  deleteBooking(id: number): Observable<any> {
    return this.http.delete(APIURL + `/bookings/${id}/`, {}).pipe(
      map((result: any) => {
        return result;
      }),
    );
  }
  cancelBooking(id: number, notification: boolean): Observable<any> {
    let param = '';
    if (notification) {
      param = `?notify_email=true`;
    }
    return this.http.post(`${APIURL}/bookings/${id}/cancel_booking/${param}`, {}).pipe(
      map((result: any) => {
        return result;
      }),
    );
  }
  completeBooking(id: number, notification: boolean): Observable<any> {
    let param = '';
    if (notification) {
      param = `?notify_email=true`;
    }
    return this.http
      .post<ApiResultResponseOfCompleteBookingResponseAndCompleteBookingStatus>(
        `${APIURL}/bookings/${id}/complete_booking/${param}`,
        {},
      )
      .pipe(
        map((result) => {
          return result;
        }),
      );
  }
  getBookingBookingPeriods(bookingId: number): Observable<BookingPeriod[]> {
    const param = '?expand=activity,resource';
    return this.http.get<BookingPeriod[]>(APIURL + `/bookings/${bookingId}/booking_periods/${param}`).pipe(
      map((result) => {
        return result;
      }),
    );
  }
  createBookingPeriod(bookingId: number, bookingPeriod: BookingPeriodExpanded): Observable<BookingPeriod> {
    return this.http.post<BookingPeriod>(APIURL + `/bookings/${bookingId}/booking_periods`, bookingPeriod).pipe(
      map((result) => {
        return result;
      }),
    );
  }
  updateBookingPeriod(
    bookingId: number,
    bookingPeriodId: number,
    // The API replaces the whole period, so a request built with toBookingPeriodRequest() fits too.
    bookingPeriod: BookingPeriodExpanded | BookingPeriodRequest,
  ): Observable<BookingPeriod> {
    return this.http
      .put<BookingPeriod>(APIURL + `/bookings/${bookingId}/booking_periods/${bookingPeriodId}`, bookingPeriod)
      .pipe(
        map((result) => {
          return result;
        }),
      );
  }
  deleteBookingPeriod(bookingId: number, bookingPeriodId: number): Observable<any> {
    return this.http.delete(APIURL + `/bookings/${bookingId}/booking_periods/${bookingPeriodId}`).pipe(
      map((result: any) => {
        return result;
      }),
    );
  }
  /*
    API: BookingPeriods
  */
  getBookingPeriod(id: number): Observable<BookingPeriodExpanded> {
    return this.http.get<BookingPeriodExpanded>(APIURL + `/bookingperiods/${id}`).pipe(
      map((result) => {
        return result;
      }),
    );
  }
  /**
   * `GET /bookingperiods`. `locationIds` takes one anlæg or several (#857), sent as one
   * `location_id` per id; none, or 0, is every anlæg the user may see.
   */
  getBookingPeriods(
    search: string,
    locationIds: number | readonly number[],
    activityId: number,
    start: string,
    end: string,
    status?: string,
    searchMode?: number,
    expand?: boolean,
  ): Observable<BookingPeriod[]> {
    let params = new HttpParams().set('start', start).set('end', end);
    if (search) {
      params = params.set('search', search);
    }
    const ids = (Array.isArray(locationIds) ? locationIds : [locationIds]).filter((id) => id > 0);
    for (const id of ids) {
      params = params.append('location_id', id);
    }
    if (activityId > 0) {
      params = params.set('activity_id', activityId);
    }
    if (status) {
      params = params.set('status_id', status);
    }
    if (searchMode) {
      params = params.set('search_mode', searchMode);
    }
    if (expand) {
      params = params.set('expand', 'bookingPeriodEntries,booking,booking.payments,activity,resource,resource.location');
    }
    return this.http.get<BookingPeriod[]>(`${APIURL}/bookingperiods`, { params });
  }
  getBookingPeriodEntries(bookingPeriodId: number): Observable<BookingPeriodEntry[]> {
    return this.http
      .get<BookingPeriodEntry[]>(APIURL + `/bookingperiods/${bookingPeriodId}/entries?expand=resourceEntry`)
      .pipe(
        map((data) => {
          return data;
        }),
        catchError(() => {
          return throwError('Something went wrong!');
        }),
      );
  }
  createBookingPeriodEntry(
    bookingPeriodId: number,
    bookingEntry: BookingPeriodEntryRequest,
  ): Observable<BookingPeriodEntry> {
    return this.http.post<BookingPeriodEntry>(`${APIURL}/bookingperiods/${bookingPeriodId}/entries`, bookingEntry).pipe(
      map((data) => {
        return data;
      }),
    );
  }
  updateBookingPeriodEntry(
    bookingPeriodId: number,
    bookingPeriodEntryId: number,
    bookingEntry: BookingPeriodEntryRequest,
  ): Observable<BookingPeriodEntry> {
    return this.http
      .put<BookingPeriodEntry>(
        `${APIURL}/bookingperiods/${bookingPeriodId}/entries/${bookingPeriodEntryId}`,
        bookingEntry,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }
  calcPrice(
    userId: number,
    resourceId: number,
    activityId: number,
    start: string,
    end: string,
    allowPartialCoverage = false,
  ): Observable<ApiResultResponseOfBookingPeriodCalculatePriceAndBookingPeriodCalculatePriceStatus> {
    let params = activityId ? `&activity_id=${activityId}` : '';
    if (userId) {
      params += `&user_id=${userId}`;
    }
    if (allowPartialCoverage) {
      params += `&allow_partial_coverage=true`;
    }
    return this.http
      .get<ApiResultResponseOfBookingPeriodCalculatePriceAndBookingPeriodCalculatePriceStatus>(
        `${APIURL}/bookingperiods/calculate_price?resource_id=${resourceId}&start=${start}&end=${end}${params}`,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }
  checkAvailability(
    resourceId: number,
    start: string,
    end: string,
    bookingId?: number | null,
    userId?: number | null,
    activityId?: number | null,
    allowPartialCoverage = false,
    bookingPeriodId?: number | null,
  ): Observable<ApiResultResponseOfBookingPeriodCheckResponseAndPeriodCheckStatus> {
    let params = bookingId ? `&booking_id=${bookingId}` : '';
    // Den gemte periode, der tjekkes igen. Serveren holder den ude af maksimumtiden: tiderne på
    // skærmen er dem, rækken allerede har, og tælles de både som brugte og ønskede, meldes en
    // sprængt kvote, som gem-stien ville acceptere.
    if (bookingPeriodId) {
      params += `&booking_period_id=${bookingPeriodId}`;
    }
    if (userId) {
      params += `&user_id=${userId}`;
    }
    if (activityId) {
      params += `&activity_id=${activityId}`;
    }
    if (allowPartialCoverage) {
      params += `&allow_partial_coverage=true`;
    }
    return this.http
      .get<ApiResultResponseOfBookingPeriodCheckResponseAndPeriodCheckStatus>(
        APIURL + `/bookingperiods/check_availability?resource_id=${resourceId}&start=${start}&end=${end}${params}`,
      )
      .pipe(
        map((data) => {
          return data;
        }),
      );
  }
  /*
    API: Codes
  */
  createCode(codeEntry: Code): Observable<Code> {
    return this.http.post<Code>(`${APIURL}/codes/`, codeEntry).pipe(map((data) => data));
  }
  /*
    API: Payments
  */
  /**
   * Open balances (Udestående). `locationIds` takes one anlæg or several (#859), sent as one
   * `location_id` per id; none, or 0, is every anlæg the user may see.
   */
  getUserBalance(userId: number, locationIds?: number | readonly number[], bookingId?: number): Observable<Payment[]> {
    let params = new HttpParams();
    const ids = (Array.isArray(locationIds) ? locationIds : [locationIds]).filter((id): id is number => id > 0);
    for (const id of ids) {
      params = params.append('location_id', id);
    }
    if (bookingId) {
      params = params.set('booking_id', bookingId);
    }
    return this.http.get<Payment[]>(`${APIURL}/payments/users/${userId}/balance`, { params });
  }
  refundPayment(refundRequest: RefundPayload): Observable<Payment> {
    return this.http.post<Payment>(`${APIURL}/payments/refund`, refundRequest).pipe(map((data) => data));
  }
  validatePSP(payload: PSPPayload): Observable<PSPResult> {
    return this.http.post<PSPResult>(`${APIURL}/payments/psp:validate`, payload).pipe(map((data) => data));
  }
  getCalendarTimeslots(params: {
    locationId: number | null;
    activityId: number | null;
    startDate: CalendarDay;
    endDate: CalendarDay;
  }): Observable<CalendarTimeslot[]> {
    let url = `${APIURL}/public/timeslots?start=${params.startDate}&end=${params.endDate}`;
    if (params.locationId) url += `&locationId=${params.locationId}`;
    if (params.activityId) url += `&activityId=${params.activityId}`;
    return this.http.get<TimeslotSlotResponse[]>(url).pipe(
      map((timeslots) =>
        timeslots.map((ts) => ({
          resourceId: ts.resourceId,
          activityId: ts.activityId,
          start: toApiDateTime(String(ts.start).slice(0, 19)),
          end: toApiDateTime(String(ts.end).slice(0, 19)),
          price: ts.price,
          isAvailable: ts.isAvailable,
          bookerTypeWarning: ts.bookerTypeWarning ?? null,
        })),
      ),
    );
  }
}
